/**
 * Created by Shikha on 2021-04-22.
 */
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {HorribleHeroesLayerConfig} from "../config/IHorribleHeroesLayerConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {Helper} from "@nolimitcity/promo-panel/bin/utils/Helper";
import {HorribleHeroesGameConfig} from "../HorribleHeroesGameConfig";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {HorribleHeroesParsedInitData} from "../server/data/HorribleHeroesParsedInitData";
import {HorribleHeroesGameMode} from "../HorribleHeroesGameMode";
import {TimelineLite} from "gsap";
import {ImgLoader} from "@nolimitcity/slot-launcher/bin/loader/ImgLoader";


export class LogoAnimations extends BaseView {
    private _layer: PIXI.Container;
    private _mayhemLogo: PIXI.spine.Spine;
    private _loadLogo: PIXI.spine.Spine;
    private _toxicLogo: PIXI.spine.Spine;
    private _logo: PIXI.Sprite;
    private _toShowLogo: boolean = true;
    private _mode: string;
    private _isSuperActivatedSpin: boolean;
    private _currentMode: string = HorribleHeroesGameMode.NORMAL;

    constructor() {
        super();
        this._layer = StageManager.getLayer(HorribleHeroesLayerConfig.LOGO.name);
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onInitDataParsed(event.params[0]));
    }

    public showLogo(mode: string): void {
        this.hideAllLogos(mode);
        this._currentMode = "" ? "NORMAL" : mode;
        switch (mode) {
            case HorribleHeroesGameMode.FREESPIN_LOW:
                this.playAnimation(this._toxicLogo, "toxicSign");
                break;
            case HorribleHeroesGameMode.FREESPIN_MID:
                this.playAnimation(this._loadLogo, "locknLoadSign");
                break;
            case "MAYHEM_MODE":
                this.playAnimation(this._mayhemLogo, "mayhemSign");
                break;
            default:
                if (this._toShowLogo) {
                    this._logo.visible = true;
                }
                this._currentMode = HorribleHeroesGameMode.NORMAL;
                break;
        }
    }

    public playAnimationsOnWin(mode: string, expandedSymbol: string): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._mode = mode;
        this._isSuperActivatedSpin = (expandedSymbol === "MX");
        this.playLogoAnimations();
        return tl;
    }

    protected initAnimations(): void {
        this._toxicLogo = this.createSpineAnimation("toxic_sign");
        this._mayhemLogo = this.createSpineAnimation("mayhem_sign");
        this._loadLogo = this.createSpineAnimation("load_sign");
        this._logo = this.createSprite("logo", [0, 0]);
        this.playLogoAnimations();
    }

    protected onResize(resizeData: IResizeData): void {
        const posX: number = HorribleHeroesGameConfig.instance.REEL_AREA_POS_X + HorribleHeroesGameConfig.instance.REEL_AREA_WIDTH / 2;
        const posY: number = -87;
        if (resizeData.orientation === Orientation.PORTRAIT) {
            this.positionLogo(this._logo, [posX, posY], 0.9);
            this.positionLogo(this._toxicLogo, [373, -55], 1);
            this.positionLogo(this._loadLogo, [373, -55], 1);
            this.positionLogo(this._mayhemLogo, [373, -55], 1);
        } else {
            this.positionLogo(this._logo, [-180, 180], 0.6);
            this.positionLogo(this._toxicLogo, [-180, 180], 0.9);
            this.positionLogo(this._loadLogo, [-180, 180], 0.9);
            this.positionLogo(this._mayhemLogo, [-180, 180], 0.9);
        }
        if (resizeData.orientation === Orientation.LANDSCAPE) {
            this._toShowLogo = !((NolimitApplication.screenBounds.left > this._logo.position.x) || !(Helper.isDefaultScreenRatio(NolimitApplication.screenBounds)));
        } else {
            const topSpace = (NolimitApplication.screenBounds.height - 720) * 0.5;
            if (topSpace >= 170) {
                this._logo.scale.set(1);
                this._toShowLogo = true;
            } else {
                this._toShowLogo = false;
            }
            this._toxicLogo.position.set(NolimitApplication.screenBounds.center + 13, -55);
            this._loadLogo.position.set(NolimitApplication.screenBounds.center + 13, -55);
            this._logo.position.set(NolimitApplication.screenBounds.center + 13, -87);
            this._mayhemLogo.position.set(NolimitApplication.screenBounds.center + 13, -55);
        }
        this.hideOrShowLogo(this._toShowLogo);
    }

    private playAnimation(logo: PIXI.spine.Spine, animName: string): void {
        if (this._toShowLogo) {
            logo.visible = true;
        } else {
            this._logo.visible = false;
        }
        logo.updateTransform();
        logo.state.setAnimation(0, animName, false);
    }

    private playLogoAnimations(): void {
        if (this._isSuperActivatedSpin) {
            this.showLogo("MAYHEM_MODE");
        } else {
            this.showLogo(this._mode);
        }
    }

    private onInitDataParsed(data: HorribleHeroesParsedInitData): void {
        this._mode = data.mode;
        this._isSuperActivatedSpin = data.getExpandedSymbol === "MX";//on restore
        EventHandler.removeEventListener(this, ServerEvent.INIT_DATA_PARSED);
    }

    private positionLogo(logo: PIXI.Sprite | PIXI.spine.Spine, pos: number[], scale: number): void {
        logo.scale.set(scale);
        logo.position.set(pos[0], pos[1]);
        this._layer.addChild(logo);
    }

    private createSpineAnimation(resourceName: string): PIXI.spine.Spine {
        const sprite: PIXI.spine.Spine = new PIXI.spine.Spine(GameResources.getSpineAsset(resourceName));
        sprite.pivot.set(0.5, 0.5);
        sprite.visible = false;
        sprite.name = resourceName.split("_")[0];
        this._layer.addChild(sprite);
        return sprite;
    }

    private createSprite(textureName: string, pos: number[]): PIXI.Sprite {
        const sprite: PIXI.Sprite = new PIXI.Sprite(ImgLoader.getImgTexture("logo"));
        sprite.anchor.set(0.5, 0.5);
        sprite.name = textureName.split("_")[0];
        sprite.position.set(pos[0], pos[1]);
        this._layer.addChild(sprite);
        return sprite;
    }

    private hideAllLogos(currentLogo: string): void {
        this._toxicLogo.visible = false;
        this._loadLogo.visible = false;
        this._mayhemLogo.visible = false;
        this._logo.visible = false;
    }

    /**to hide logo if 4:3 ratio */
    private hideOrShowLogo(toShow: boolean): void {
        switch (this._currentMode) {
            case "MAYHEM_MODE":
                this._mayhemLogo.visible = toShow;
                break;
            case HorribleHeroesGameMode.FREESPIN_MID:
                this._loadLogo.visible = toShow;
                break;
            case HorribleHeroesGameMode.FREESPIN_LOW:
                this._toxicLogo.visible = toShow;
                break;
            default:
                if (this._currentMode === HorribleHeroesGameMode.NORMAL) {
                    this._logo.visible = toShow;
                }
                break;
        }
    }
}